import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Heart } from 'lucide-react';
import Footer from '../components/Footer';

export default function ThanksPage() {
  const [searchParams] = useSearchParams();
  const tipId = searchParams.get('tip');
  const sessionId = searchParams.get('session_id');
  const paymentIntent = searchParams.get('payment_intent');

  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [tipDetails, setTipDetails] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    // We accept three flows:
    //  1. New embedded PaymentIntent flow: ?tip=<id>&payment_intent=<pi_...>
    //     → poll GET /tips/{tipId} until status === "paid" (webhook updates it).
    //  2. Legacy Stripe Checkout redirect: ?session_id=<cs_...>&tip=<id>
    //     → poll GET /tips/status/{session_id}.
    //  3. No identifiers → failed.
    if (!sessionId && !paymentIntent) {
      setStatus('failed');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;
    let timer = null;

    const fetchTipDetails = async () => {
      if (!tipId) return;
      try {
        const response = await fetch(`${API}/tips/${tipId}`);
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setTipDetails(data);
        }
      } catch (error) {
        console.error('Error fetching tip:', error);
      }
    };

    const pollByTipId = async () => {
      if (cancelled) return;
      if (attempts >= maxAttempts) {
        // Stripe Elements has already confirmed the payment client-side,
        // so even if the webhook hasn't reconciled the DB yet, the
        // payment is real. Show success and the tip will catch up.
        setStatus('success');
        fetchTipDetails();
        return;
      }
      try {
        const response = await fetch(`${API}/tips/${tipId}`);
        if (!response.ok) throw new Error('Failed to load tip');
        const data = await response.json();
        if (cancelled) return;
        if (data.status === 'paid') {
          setStatus('success');
          setTipDetails(data);
          return;
        }
        if (data.status === 'failed') {
          setStatus('failed');
          return;
        }
        attempts += 1;
        timer = setTimeout(pollByTipId, pollInterval);
      } catch (error) {
        console.error('Error polling tip:', error);
        if (cancelled) return;
        setStatus('success');
        fetchTipDetails();
      }
    };

    const pollBySessionId = async () => {
      if (cancelled) return;
      if (attempts >= maxAttempts) {
        setStatus('success');
        fetchTipDetails();
        return;
      }
      try {
        const response = await fetch(`${API}/tips/status/${sessionId}`);
        if (!response.ok) throw new Error('Failed to check status');
        const data = await response.json();
        if (cancelled) return;
        if (data.paymentStatus === 'paid') {
          setStatus('success');
          fetchTipDetails();
          return;
        }
        if (data.status === 'expired') {
          setStatus('failed');
          return;
        }
        attempts += 1;
        timer = setTimeout(pollBySessionId, pollInterval);
      } catch (error) {
        console.error('Error checking status:', error);
        if (cancelled) return;
        setStatus('success');
        fetchTipDetails();
      }
    };

    if (paymentIntent && tipId) {
      pollByTipId();
    } else if (sessionId) {
      pollBySessionId();
    } else {
      setStatus('failed');
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, paymentIntent, tipId, API]);

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  if (status === 'loading') {
    return (
      <div className="success-page" data-testid="thanks-page-loading">
        <div className="success-icon">
          <Loader2 size={40} className="text-[#F5C542] animate-spin" />
        </div>
        <h1 className="text-2xl font-heading font-bold mb-2">Processing payment...</h1>
        <p className="text-[#A1A1AA]">Please wait while we confirm your tip.</p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="success-page" data-testid="thanks-page-failed">
        <div className="success-icon" style={{ background: 'rgba(255, 69, 58, 0.15)' }}>
          <XCircle size={40} className="text-red-500" />
        </div>
        <h1 className="text-2xl font-heading font-bold mb-2">Payment Failed</h1>
        <p className="text-[#A1A1AA] mb-6">Something went wrong with your payment. Please try again.</p>
        <button 
          onClick={() => window.history.back()}
          className="btn-secondary"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="success-page" data-testid="thanks-page-success">
      <div className="success-icon animate-pulse-gold">
        <CheckCircle size={40} className="text-[#F5C542]" />
      </div>

      <h1 className="text-3xl font-heading font-bold mb-2" data-testid="thanks-heading">
        Your tip was sent! 🎉
      </h1>

      {tipDetails && (
        <div className="bg-white/5 rounded-2xl px-8 py-6 mb-6 max-w-sm w-full" data-testid="thanks-summary">
          <p
            className="text-4xl font-heading font-bold text-[#F5C542] mb-2"
            data-testid="thanks-amount"
          >
            {formatCurrency(tipDetails.amountCents)}
          </p>
          <p className="text-[#A1A1AA]">
            sent to{' '}
            <span
              className="text-white font-semibold"
              data-testid="thanks-recipient-name"
            >
              {tipDetails.recipientName}
            </span>
          </p>
          {tipDetails.praiseTag && (
            <div className="mt-4 inline-flex items-center gap-2 bg-[#F5C542]/10 text-[#F5C542] px-3 py-1.5 rounded-full text-sm font-medium">
              <Heart size={14} />
              {tipDetails.praiseTag}
            </div>
          )}
        </div>
      )}

      {/* No CTA, no link back to the landing page. The tipper is done — we
          don't want them to feel recruited. A quiet line is enough. */}
      <p className="text-[#52525B] text-sm">You can close this tab.</p>
      <Footer className="mt-10 w-full max-w-md" />
    </div>
  );
}
